/**
 * @file MainView.jsx
 * @author Fernando Ruano
 * @description Main View Page
 */

// Dependencies
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Empty } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { getStorageItem } from '@nephro/commons/helpers/storage-helper';

// Helpers
import { MY_RECOMMENDATIONS_INDICATOR } from '@nephro/pages/Indicators/helpers/constants';
import usePatientIndicators from '@nephro/pages/Indicators/helpers/usePatientIndicators';
import { sortByDate, getFormatedDate } from '@nephro/commons/helpers/utils';
import { capitalizeFirstLetter } from '@nephro/commons/helpers/string-helper';

// Styles
import styles from './styles.module.css';

function MainView({ patientActions, actions }) {
  const myRecommendations = usePatientIndicators(MY_RECOMMENDATIONS_INDICATOR);
  const { t: translate } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const [recommendationsList, setRecommendationsList] = React.useState([]);
  const [patientData, setPatientData] = React.useState(0);

  const { getPatientExtraDataById } = patientActions;
  const { setLoading } = actions;

  //  Function to get and set patient full info
  const onGetFullPatientInfo = (_, response) => {
    if (response?.data) {
      setPatientData({
        ...response?.data
      });
      setLoading(false);
    }
  };
  const onGetProfileInfo = async () => {
    const patientInfo = await getStorageItem('patientInfo');
    if (patientInfo?.id_patient) {
      const data = { id: patientInfo.id_patient };
      getPatientExtraDataById(data, onGetFullPatientInfo);
    }
  };

  useEffect(() => {
    onGetProfileInfo();
    if (myRecommendations.length) {
      const sortedList = sortByDate(myRecommendations, 'fecha');
      setRecommendationsList(sortedList);
    }
  }, [myRecommendations]);

  const onGetCapitalDate = (date) => {
    const day = getFormatedDate(date, 'D');
    const month = capitalizeFirstLetter(
      getFormatedDate(date, 'MMMM').toString()
    );
    const year = getFormatedDate(date, 'YYYY');

    return `${day} ${month}, ${year}`;
  };

  const onGetRecommendationType = (param) => {
    switch (param) {
      case 17:
        return 'Plan General';
      case 18:
        return 'Recomendaciones para el paciente';
      default:
        return null;
    }
  };

  return recommendationsList.length ? (
    <div className="p-5 items-center text-baxterGray">
      <p className="text-left text-base">
        {translate('indicators.myRecommendations.title')}
      </p>
      <div className="pt-2">
        {recommendationsList.map((itm, idx) => {
          return (
            <div
              key={idx}
              onClick={() =>
                history.push(`${location.pathname}/details`, {
                  formatedDate: onGetCapitalDate(itm.fecha),
                  ...itm,
                  type: onGetRecommendationType(itm.parametro)
                })
              }
              className="flex flex-row justify-between items-center rounded-md border border-grayBorders p-2 mb-4 md:max-w-45p">
              <div>
                <h2 className="mb-2 text-base font-bold text-baxterGray">
                  {onGetRecommendationType(itm.parametro)}
                </h2>
                <h2 className="mb-2 text-sm font-bold text-baxterGray">
                  {onGetCapitalDate(itm.fecha)}
                </h2>
                <pre className={styles.ellipsisMultiLine}>
                  {itm.valor_texto}
                </pre>
              </div>
              <div>
                <ArrowRightOutlined
                  style={{
                    color: 'var(--color-grayFontInput)',
                    fontSize: 20
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <h3 className="text-left mb-4 text-baxterGray text-base">
        En caso de dudas, comunícate con tu centro de atención médica
        <strong> {patientData?.centro ?? 'NA'}</strong>
      </h3>
    </div>
  ) : (
    <Empty
      style={{ color: '#858fab', fontSize: 15, marginTop: 160 }}
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={translate('commons.noRecommendations')}
    />
  );
}

/**
 * Component definition of proptypes.
 */
MainView.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  patientActions: PropTypes.shape({
    getPatientExtraDataById: PropTypes.func.isRequired
  }).isRequired,
  loading: PropTypes.bool
};

export default MainView;
